import { Fragment } from 'react';
import camelCase from 'lodash/camelCase';
import {
  SelectInput,
  TextInput,
  NumberInput,
  CheckboxInput,
  PhoneNumberInput,
  DatePickerInput,
} from '@thinknet-gmbh/platform-common-ui';

import { STREET_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import { Rule } from 'antd/lib/form/';
import NumberFormat from 'react-number-format';

import {
  PERSONAL_INFO_QUESTIONNAIRES,
  SECURITY_INFORMATION_QUESTIONNAIRES,
  DEFAULT_NAME_FIELD_REGEX,
  SCHOOL_INFORMATION_QUESTIONNAIRES,
  PAYMENT_INFORMATION_QUESTIONNAIRES,
  FINISH_INFORMATION_QUESTIONNAIRES,
  GendersEnum,
  MaritalStatusesEnum,
  SalutationOptionsEnum,
  HealthInsuranceTypesEnum,
  SchoolOptionsEnum,
  HighestTrainingDegreeEnum,
  HOUSE_NUMBER_FIELD_REGEX,
} from '@thinknet-gmbh/platform-common';

import { FORM_ITEMS_TYPE, QUESTIONNAIRE_FORM_STEPS } from '../../questionnaire-page.constants';
import { IEmployeeQuestionnaireSetting } from '../../../../pages/settings/components/employee-questionnaire-settings/components/change-employee-questionnaire-settings/employee-questionnaire-settings-interfaces';
import { generateStepArray } from 'src/common/helpers/common.helpers';

const getPersonalInfoData = ({ t, i18n }: { t: Function; i18n: any }) => {
  return {
    [PERSONAL_INFO_QUESTIONNAIRES.firstName]: {
      name: ['employeePersonalInfo', 'firstName'],
      label: t('common:labels.firstName'),
      rules: [
        { required: true, message: t('common:validation.error.firstNameRequired') },
        {
          pattern: DEFAULT_NAME_FIELD_REGEX,
          message: t('common:validation.error.incorrectFormat'),
        },
      ],
      inputProps: {
        placeholder: 'Kate',
        maxLength: 30,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.lastName]: {
      name: ['employeePersonalInfo', 'lastName'],
      label: t('common:labels.lastName'),
      rules: [
        { required: true, message: t('common:validation.error.lastNameRequired') },
        {
          pattern: DEFAULT_NAME_FIELD_REGEX,
          message: t('common:validation.error.incorrectFormat'),
        },
      ],
      inputProps: {
        placeholder: 'Olov',
        maxLength: 30,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.birthName]: {
      name: ['employeePersonalInfo', 'birthName'],
      label: t('employee:employeePersonalInfo.labels.birthName'),
      inputProps: {
        placeholder: 'Kate',
        maxLength: 30,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.cityOfBirth]: {
      name: ['employeePersonalInfo', 'cityOfBirth'],
      label: t('employee:employeePersonalInfo.labels.cityOfBirth'),
      inputProps: {
        placeholder: 'Hamburg',
        maxLength: 34,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.countryOfBirth]: {
      name: ['employeePersonalInfo', 'countryOfBirth'],
      label: t('employee:employeePersonalInfo.labels.countryOfBirth'),
      inputProps: {
        placeholder: t('common:placeholders.country'),
        maxLength: 50,
      },
      inputType: FORM_ITEMS_TYPE.country,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.gender]: {
      name: ['employeePersonalInfo', 'gender'],
      label: t('employee:employeePersonalInfo.labels.gender'),
      rules: [
        {
          required: true,
          message: t('employee:employeePersonalInfo.validation.genderRequired'),
        },
      ],
      inputProps: {
        placeholder: t('employee:employeePersonalInfo.labels.female'),
      },
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: Object.keys(GendersEnum).map(gender => ({
        label: t(`employee:employeePersonalInfo.labels.${gender.toLowerCase()}`),
        value: gender,
      })),
    },
    [PERSONAL_INFO_QUESTIONNAIRES.salutation]: {
      name: ['employeePersonalInfo', 'salutation'],
      label: t('employee:employeePersonalInfo.labels.salutation'),
      rules: [
        {
          required: true,
          message: t('customers:error.fieldIsRequired'),
        },
      ],
      inputProps: {
        placeholder: t('employee:employeePersonalInfo.labels.salutationOptions.MR'),
      },
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: Object.keys(SalutationOptionsEnum).map(option => ({
        label: t(`employee:employeePersonalInfo.labels.salutationOptions.${option}`),
        value: option,
      })),
    },
    [PERSONAL_INFO_QUESTIONNAIRES.street]: {
      name: ['employeePersonalInfo', 'street'],
      label: t('common:labels.address.street'),
      rules: [
        { required: true, message: t('customers:error.fieldIsRequired') },
        {
          pattern: STREET_FIELD_REGEX,
          message: t('common:validation.error.streetFormat'),
        },
      ],
      inputProps: {
        placeholder: t('common:placeholders.street'),
        maxLength: 33,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.houseNumber]: {
      name: ['employeePersonalInfo', 'houseNumber'],
      label: t('common:labels.address.houseNumber'),
      rules: [
        { required: true, message: t('customers:error.fieldIsRequired') },
        {
          pattern: HOUSE_NUMBER_FIELD_REGEX,
          message: t('common:validation.error.houseNumberFormat'),
        },
      ],
      inputProps: {
        placeholder: '25',
        maxLength: 9,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.zipCode]: {
      name: ['employeePersonalInfo', 'zipCode'],
      label: t('common:labels.address.zipCode'),
      rules: [
        { required: true, message: t('customers:error.fieldIsRequired') },
        { type: 'number', message: t('common:validation.error.mustBeInteger') },
        {
          validator: (_: any, value: string | number) =>
            Number.isInteger(value)
              ? Promise.resolve()
              : Promise.reject(new Error(t('common:validation.error.mustBeInteger'))),
        },
      ] as Rule[],
      inputProps: {
        placeholder: '27321',
        maxLength: 5,
        allowLeadingZeros: false,
        thousandSeparator: false,
        customInput: TextInput,
      },
      InputComponent: NumberInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.city]: {
      name: ['employeePersonalInfo', 'city'],
      label: t('common:labels.address.city'),
      rules: [{ required: true, message: t('customers:error.fieldIsRequired') }],
      inputProps: {
        placeholder: 'Hamburg',
        maxLength: 34,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.country]: {
      name: ['employeePersonalInfo', 'country'],
      label: t('common:labels.address.country'),
      rules: [{ required: true, message: t('customers:error.fieldIsRequired') }],
      inputProps: {
        placeholder: t('common:placeholders.country'),
        maxLength: 50,
      },
      inputType: FORM_ITEMS_TYPE.country,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.academicDegree]: {
      name: ['employeePersonalInfo', 'academicDegree'],
      label: t('employee:employeePersonalInfo.labels.academicDegree'),
      inputProps: {
        placeholder: t('employee:employeePersonalInfo.labels.professor'),
        maxLength: 20,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.phoneNumber]: {
      name: ['employeePersonalInfo', 'phoneNumber'],
      label: t('employee:labels.personalPhoneNumber'),
      inputProps: {
        placeholder: '+49 176 384 15749',
      },
      InputComponent: PhoneNumberInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.personalEmail]: {
      name: ['employeePersonalInfo', 'personalEmail'],
      label: t('employee:labels.personalEmail'),
      rules: [
        { type: 'email', message: t('common:validation.error.incorrectFormat') },
        { required: true, message: t('employee:validationError.personalEmailRequired') },
      ] as Rule[],
      inputProps: {
        placeholder: 'kateonolov@gmail.com',
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.birthday]: {
      name: ['employeePersonalInfo', 'birthday'],
      label: t('employee:labels.birthday'),
      rules: [{ required: true, message: t('employee:validationError.birthday') }],
      inputProps: {
        lang: i18n.language,
        format: 'DD.MM.YYYY',
      },
      InputComponent: DatePickerInput,
      inputType: FORM_ITEMS_TYPE.date,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.socialsLinkedIn]: {
      name: ['employeePersonalInfo', 'socials', 'linkedIn'],
      label: 'Linkedin',
      inputProps: {
        placeholder: 'http://linkedin/....',
        maxLength: 100,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.nationality]: {
      name: ['employeePersonalInfo', 'nationality'],
      label: t('employee:labels.nationality'),
      rules: [
        { required: true, message: t('employee:validationError.nationalityRequired') },
        {
          pattern: DEFAULT_NAME_FIELD_REGEX,
          message: t('common:validation.error.incorrectFormat'),
        },
      ],
      inputProps: {
        placeholder: t('common:placeholders.country'),
        maxLength: 50,
      },
      inputType: FORM_ITEMS_TYPE.country,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.socialsXing]: {
      name: ['employeePersonalInfo', 'socials', 'xing'],
      label: 'XING',
      inputProps: {
        placeholder: 'http://xing/....',
        maxLength: 100,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PERSONAL_INFO_QUESTIONNAIRES.imagePersonalIdCard]: {
      inputType: FORM_ITEMS_TYPE.personalIdCard,
    },
  };
};
const getEmergencyContactData = ({ t }: { t: Function }) => {
  return {
    [SECURITY_INFORMATION_QUESTIONNAIRES.emergencyContactName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.emergencyContact, 'name'],
      label: t('emergencyDetails.labels.emergencyName'),
      rules: [
        { required: true, message: t('emergencyDetails.validation.emergencyNameRequired') },
        {
          pattern: DEFAULT_NAME_FIELD_REGEX,
          message: t('common:validation.error.incorrectFormat'),
        },
      ],
      inputProps: {
        placeholder: t('emergencyDetails.placeholders.emergencyName'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SECURITY_INFORMATION_QUESTIONNAIRES.emergencyContactPhoneNumber]: {
      name: [QUESTIONNAIRE_FORM_STEPS.emergencyContact, 'phoneNumber'],
      label: t('emergencyDetails.labels.emergencyPhoneNumber'),
      rules: [
        { required: true, message: t('emergencyDetails.validation.emergencyPhoneNumberRequired') },
      ],
      inputProps: {
        placeholder: '+49 176 384 15749',
      },
      InputComponent: PhoneNumberInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SECURITY_INFORMATION_QUESTIONNAIRES.emergencyContactRelationship]: {
      name: [QUESTIONNAIRE_FORM_STEPS.emergencyContact, 'relationship'],
      label: t('emergencyDetails.labels.emergencyRelationship'),
      rules: [
        { required: true, message: t('emergencyDetails.validation.emergencyContactRequired') },
      ],
      inputProps: {
        placeholder: t('emergencyDetails.placeholders.emergencyRelationship'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SECURITY_INFORMATION_QUESTIONNAIRES.maritalStatus]: {
      name: [QUESTIONNAIRE_FORM_STEPS.emergencyContact, 'maritalStatus'],
      label: t('emergencyDetails.labels.maritalStatus'),
      inputProps: {
        placeholder: t('emergencyDetails.labels.married'),
      },
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: Object.keys(MaritalStatusesEnum).map(status => ({
        label: t(`emergencyDetails.labels.${status.toLowerCase()}`),
        value: status,
      })),
    },
    [SECURITY_INFORMATION_QUESTIONNAIRES.childAllowance]: {
      name: [QUESTIONNAIRE_FORM_STEPS.emergencyContact, 'childAllowance'],
      label: t('emergencyDetails.labels.childAllowance'),
      inputProps: {
        placeholder: t('emergencyDetails.labels.noChildren'),
      },
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: generateStepArray(0, 10, 0.5).map(step => ({
        label: step,
        value: step,
      })),
    },
  };
};

const getEducationAndSkillsData = ({ t }: { t: Function }) => {
  return {
    [SCHOOL_INFORMATION_QUESTIONNAIRES.schoolName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'schoolName'],
      label: t('educationsAndSkills.labels.schoolName'),
      inputProps: {
        placeholder: t('educationsAndSkills.placeholders.schoolName'),
        maxLength: 50,
        dropdownMatchSelectWidth: false,
      },
      inputType: FORM_ITEMS_TYPE.select,
      InputComponent: SelectInput,
      itemWidth: 400,
      selectOptions: Object.keys(SchoolOptionsEnum).map(name => ({
        label: t(`employee:labels.schoolOptions.${camelCase(name.toLowerCase())}`),
        value: name,
      })),
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.universityName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'universityName'],
      label: t('educationsAndSkills.labels.universityName'),
      inputProps: {
        placeholder: t('educationsAndSkills.placeholders.universityName'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.furtherName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'furtherName'],
      label: t('educationsAndSkills.labels.furtherName'),
      inputProps: {
        placeholder: t('educationsAndSkills.placeholders.furtherName'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.driversLicenses]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'driversLicenses'],
      label: t('educationsAndSkills.labels.driversLicenses'),
      inputProps: {
        placeholder: t('common:labels.yes'),
      },
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: [
        {
          label: t('common:labels.yes'),
          value: true,
        },
        {
          label: t('common:labels.no'),
          value: false,
        },
      ],
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.trainingName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'trainingName'],
      label: t('educationsAndSkills.labels.trainingName'),
      inputProps: {
        placeholder: t('educationsAndSkills.placeholders.trainingName'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.highestTrainingDegree]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'highestTrainingDegree'],
      label: t('educationsAndSkills.labels.highestTrainingDegree'),
      inputProps: {
        placeholder: t('educationsAndSkills.placeholders.highestTrainingDegree'),
        maxLength: 50,
        dropdownMatchSelectWidth: false,
      },
      itemWidth: 400,
      InputComponent: SelectInput,
      inputType: FORM_ITEMS_TYPE.select,
      selectOptions: Object.keys(HighestTrainingDegreeEnum).map(name => ({
        label: t(`employee:labels.highestTrainingOptions.${camelCase(name.toLowerCase())}`),
        value: name,
      })),
    },
    [SCHOOL_INFORMATION_QUESTIONNAIRES.skills]: {
      name: [QUESTIONNAIRE_FORM_STEPS.educationAndSkills, 'skills'],
      label: t('educationsAndSkills.labels.skills'),
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.customSkillsComponent,
    },
  };
};

const getPaymentAndInsuranceData = ({
  t,
  i18n,
  healthInsurance = [],
}: {
  t: Function;
  i18n: any;
  healthInsurance?: {
    _id: string;
    name: string;
  }[];
}) => {
  const healthInsuranceOptions = healthInsurance?.map((el: { name: string; _id: string }) => ({
    value: el._id,
    label: el.name,
  }));

  return {
    [PAYMENT_INFORMATION_QUESTIONNAIRES.bankAccountHolder]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'bankAccountHolder'],
      label: t('paymentAndInsurance.labels.accountHolder'),
      rules: [
        {
          required: true,
          message: t('paymentAndInsurance.validation.accountHolderRequired'),
        },
        {
          pattern: DEFAULT_NAME_FIELD_REGEX,
          message: t('common:validation.error.incorrectFormat'),
        },
      ],
      inputProps: {
        placeholder: t('paymentAndInsurance.placeholders.bankAccountHolder'),
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.iban]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'iban'],
      label: 'IBAN',
      validateTrigger: [],
      rules: [
        {
          required: true,
          message: t('paymentAndInsurance.validation.ibanRequired'),
        },
        { len: 22, message: t('paymentAndInsurance.validation.ibanInvalidLength') },
      ],
      inputProps: {
        customInput: TextInput,
        prefix: 'DE',
        isNumericString: true,
        maxLength: 22,
        placeholder: 'DE89370400440532013000',
      },
      InputComponent: NumberFormat,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.bic]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'bic'],
      label: 'BIC',
      validateTrigger: [],
      rules: [
        {
          required: true,
          message: t('paymentAndInsurance.validation.bicRequired'),
        },
        {
          pattern: /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/,
          message: t('paymentAndInsurance.validation.bicInvalidLength'),
        },
      ],
      inputProps: {
        placeholder: 'AABSDE31',
        maxLength: 11,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.typeOfVisa]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'typeOfVisa'],
      label: t('paymentAndInsurance.labels.typeOfVisa'),
      inputProps: {
        placeholder: 'B-1',
        maxLength: 50,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.visaExpirationDate]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'visaExpirationDate'],
      label: t('paymentAndInsurance.labels.visaExpiredDate'),
      inputProps: {
        format: 'DD.MM.YYYY',
        lang: i18n.language,
      },
      InputComponent: DatePickerInput,
      inputType: FORM_ITEMS_TYPE.date,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.workPermissionExpireDate]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'workPermissionExpireDate'],
      label: t('paymentAndInsurance.labels.workPermissionExpireDate'),
      inputProps: {
        format: 'DD.MM.YYYY',
        lang: i18n.language,
      },
      inputType: FORM_ITEMS_TYPE.withSwitcher,
      switcherName: 'workPermissionNeeded',
      fieldName: 'workPermissionExpireDate',
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.residencePermissionExpireDate]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'residencePermissionExpireDate'],
      label: t('paymentAndInsurance.labels.residencePermissionExpireDate'),
      inputProps: {
        format: 'DD.MM.YYYY',
        lang: i18n.language,
      },
      inputType: FORM_ITEMS_TYPE.withSwitcher,
      switcherName: 'residencePermissionNeeded',
      fieldName: 'residencePermissionExpireDate',
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.certificateOfStudyExpireDate]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'certificateOfStudyExpireDate'],
      label: t('paymentAndInsurance.labels.certificateOfStudyExpireDate'),
      inputProps: {
        format: 'DD.MM.YYYY',
        lang: i18n.language,
      },
      inputType: FORM_ITEMS_TYPE.withSwitcher,
      switcherName: 'hasCertificateOfStudy',
      fieldName: 'certificateOfStudyExpireDate',
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.nationalInsuranceNumber]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'nationalInsuranceNumber'],
      label: t('paymentAndInsurance.labels.insuranceNumber'),
      validateTrigger: [],
      rules: [
        {
          pattern: /^\d{8}[A-Z]\d{3}$/,
          message: t('paymentAndInsurance.validation.invalidInsuranceNumber'),
        },
      ],
      inputProps: {
        placeholder: '12345678A900',
        maxLength: 12,
      },
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.taxId]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'taxId'],
      label: t('paymentAndInsurance.labels.taxId'),
      validateTrigger: [],
      rules: [
        { required: true, message: t('customers:error.fieldIsRequired') },
        {
          validator: (_: any, value: any) =>
            !value || String(value).length === 11
              ? Promise.resolve()
              : Promise.reject(new Error(t('paymentAndInsurance.validation.taxIdInvalidLength'))),
        },
      ],
      inputProps: {
        placeholder: t('paymentAndInsurance.placeholders.taxId'),
        maxLength: 11,
        thousandSeparator: false,
        customInput: TextInput,
      },
      InputComponent: NumberInput,
      inputType: FORM_ITEMS_TYPE.text,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.healthInsuranceType]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'healthInsuranceType'],
      label: t('paymentAndInsurance.labels.healthInsuranceType'),
      inputProps: {
        placeholder: t('paymentAndInsurance.labels.insurancePlan'),
        maxLength: 50,
      },
      InputComponent: SelectInput,
      selectOptions: Object.keys(HealthInsuranceTypesEnum).map(type => ({
        label: t(`paymentAndInsurance.labels.healthInsuranceOptions.${type}`),
        value: type,
      })),
      inputType: FORM_ITEMS_TYPE.select,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.healthInsuranceName]: {
      name: [QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance, 'healthInsuranceId'],
      label: t('paymentAndInsurance.labels.healthInsuranceName'),
      inputProps: {
        placeholder: t('paymentAndInsurance.placeholders.healthInsuranceName'),
        options: healthInsuranceOptions,
      },
      inputType: FORM_ITEMS_TYPE.autocomplete,
    },
    [PAYMENT_INFORMATION_QUESTIONNAIRES.imageHealthInsuranceCard]: {
      inputType: FORM_ITEMS_TYPE.healthInsuranceCard,
    },
  };
};

const getFinishInformationData = ({
  t,
  privacyPolicyLink,
  documentLink,
}: {
  t: Function;
  privacyPolicyLink?: string;
  documentLink?: string;
}) => {
  return {
    [FINISH_INFORMATION_QUESTIONNAIRES.accuracyConfirmation]: {
      name: [QUESTIONNAIRE_FORM_STEPS.finishInformation, 'accuracyConfirmation'],
      label: t('questionnaire.labels.accuracyConfirmation'),
      InputComponent: CheckboxInput,
      rules: [
        {
          validator: (_: any, value: any) =>
            value
              ? Promise.resolve()
              : Promise.reject(new Error(t('questionnaire.validation.accuracyConfirmation'))),
        },
      ],
      inputType: FORM_ITEMS_TYPE.checkbox,
    },
    [FINISH_INFORMATION_QUESTIONNAIRES.privacyPolicyConfirmation]: {
      name: [QUESTIONNAIRE_FORM_STEPS.finishInformation, 'privacyPolicyConfirmation'],
      label: (
        <Fragment>
          {t('questionnaire.labels.privacyPolicy.partOne')}
          {privacyPolicyLink ? (
            <a target="_blank" rel="noopener noreferrer" href={privacyPolicyLink}>
              {t('questionnaire.labels.privacyPolicy.link')}
            </a>
          ) : (
            ''
          )}
          {t('questionnaire.labels.privacyPolicy.partTwo')}
        </Fragment>
      ),
      InputComponent: CheckboxInput,
      rules: [
        {
          validator: (_: any, value: any) =>
            value
              ? Promise.resolve()
              : Promise.reject(new Error(t('questionnaire.validation.privacyPolicyConfirmation'))),
        },
      ],
      inputType: FORM_ITEMS_TYPE.checkbox,
    },
    [FINISH_INFORMATION_QUESTIONNAIRES.miniJobDocument]: {
      name: [QUESTIONNAIRE_FORM_STEPS.finishInformation, 'miniJobDocument'],
      label: (
        <Fragment>
          <a target="_blank" rel="noopener noreferrer" href={documentLink} title={'Document'}>
            {t(`employee:questionnaire.finishInformation.miniJobDocumentLink`)}
          </a>
        </Fragment>
      ),
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.miniJobDocument,
    },
    [FINISH_INFORMATION_QUESTIONNAIRES.signature]: {
      name: 'signatureId',
      label: t(`employee:questionnaire.finishInformation.signature`),
      rules: [
        {
          required: true,
          message: t('employee:questionnaire.validation.signature'),
        },
      ],
      InputComponent: TextInput,
      inputType: FORM_ITEMS_TYPE.signature,
    },
  };
};

export const getStepsFormFieldsData = ({
  t,
  i18n,
  formType,
  questionnaireSetting,
  healthInsurance,
}: {
  t: Function;
  i18n: any;
  formType: string;
  questionnaireSetting?: IEmployeeQuestionnaireSetting;
  healthInsurance?: {
    name: string;
    _id: string;
  }[];
}) => {
  const { privacyPolicyLink, miniJobDocument } = questionnaireSetting || {};

  const documentLink = miniJobDocument?.location || '';

  switch (formType) {
    case QUESTIONNAIRE_FORM_STEPS.employeePersonalInfo:
      return getPersonalInfoData({ t, i18n });
    case QUESTIONNAIRE_FORM_STEPS.emergencyContact:
      return getEmergencyContactData({ t });
    case QUESTIONNAIRE_FORM_STEPS.educationAndSkills:
      return getEducationAndSkillsData({ t });
    case QUESTIONNAIRE_FORM_STEPS.paymentAndInsurance:
      return getPaymentAndInsuranceData({ t, i18n, healthInsurance });
    case QUESTIONNAIRE_FORM_STEPS.finishInformation:
      return getFinishInformationData({ t, privacyPolicyLink, documentLink });
    default:
      return {};
  }
};
